import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, ShieldCheck, Clock, Users, Search, Sparkles } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminFreelanceProfiles (V3.16)
 * ──────────────────────────────
 * Vue Super Admin listant les profils Freelance en PENDING/VERIFIED
 * conformément à la règle V3.16 (DIGITAL → VERIFIED immédiat, TERRAIN
 * → PENDING tant que avatar + bio + 2 photos portfolio manquent).
 */
export default function AdminFreelanceProfiles() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, verified: 0, total: 0 });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = "/v1/admin/freelance-profiles" + (status ? `?status=${status}` : "");
      const { data } = await api.get(url);
      setItems(data.items || []);
      setCounts(data.counts || { pending: 0, verified: 0, total: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/v1/admin/freelance-profiles/backfill");
      toast.success(`Recalcul terminé (${data.processed} profils traités)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBackfilling(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(needle) ||
      (it.metier || "").toLowerCase().includes(needle) ||
      (it.user_id || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  if (!isAdmin) {
    return (
      <PrivateLayout title="Profils Freelance">
        <div className="afr-empty" data-testid="freelance-admin-forbidden">
          <div className="afr-empty__title">Réservé aux administrateurs</div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="⭐ Profils Freelance — Statuts (V3.16)"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Profils Freelance" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="admin-freelance-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          <button
            className="afr-btn afr-btn--primary afr-btn--sm"
            onClick={runBackfill}
            disabled={backfilling}
            data-testid="admin-freelance-backfill"
          >
            {backfilling ? "…" : <><Sparkles size={12} /> Recalculer</>}
          </button>
          <Link to="/admin/command" className="afr-btn afr-btn--outline afr-btn--sm">
            <ChevronLeft size={14} /> Admin
          </Link>
        </div>
      }
    >
      <div data-testid="admin-freelance-profiles-page">
        {/* ═══ KPIs ═══ */}
        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}
          data-testid="admin-freelance-kpis"
        >
          <Kpi label="Total profils" value={counts.total} icon={Users} color="#0F2A42" testid="admin-freelance-kpi-total" />
          <Kpi label="En attente (PENDING)" value={counts.pending} icon={Clock} color="#B45309" testid="admin-freelance-kpi-pending" />
          <Kpi label="Vérifiés (VERIFIED)" value={counts.verified} icon={ShieldCheck} color="#009640" testid="admin-freelance-kpi-verified" />
        </div>

        {/* ═══ Filtres ═══ */}
        <section className="afr-card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4 }} data-testid="admin-freelance-status-filter">
              {[
                { v: "", label: "Tous" },
                { v: "PENDING", label: "En attente" },
                { v: "VERIFIED", label: "Vérifiés" },
              ].map((opt) => (
                <button
                  key={opt.v || "all"}
                  className={`afr-btn afr-btn--sm ${status === opt.v ? "afr-btn--primary" : "afr-btn--outline"}`}
                  onClick={() => setStatus(opt.v)}
                  data-testid={`admin-freelance-filter-${opt.v || "all"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200 }}>
              <Search size={14} style={{ position: "absolute", top: 9, left: 8, color: "#94a3b8" }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher nom, métier, ID…"
                data-testid="admin-freelance-search"
                style={{
                  width: "100%", padding: "6px 8px 6px 28px",
                  border: "1px solid var(--afr-border,#E2E8F0)", borderRadius: 6, fontSize: "0.85rem",
                }}
              />
            </div>
          </div>
        </section>

        {/* ═══ Tableau ═══ */}
        <section className="afr-card" style={{ padding: 12 }} data-testid="admin-freelance-table-card">
          {loading ? (
            <div data-testid="admin-freelance-loading">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="afr-empty" data-testid="admin-freelance-empty">
              <div className="afr-empty__title">Aucun profil Freelance à afficher</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ background: "rgba(15,42,66,0.04)" }}>
                    <th style={th}>Utilisateur</th>
                    <th style={th}>Métier</th>
                    <th style={th}>Catégorie</th>
                    <th style={th}>Statut</th>
                    <th style={th}>Complétude</th>
                    <th style={th}>MAJ</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.user_id} data-testid={`admin-freelance-row-${it.user_id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{it.name || "—"}</div>
                        <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{it.user_id}</div>
                      </td>
                      <td style={td}>{it.metier || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                      <td style={td}>
                        {it.job_category_code ? (
                          <span
                            style={{
                              padding: "2px 8px",
                              background: it.job_category_code === "DIGITAL" ? "#DBEAFE" : "#FEF3C7",
                              color: it.job_category_code === "DIGITAL" ? "#1E40AF" : "#78350F",
                              borderRadius: 999,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                            }}
                          >
                            {it.job_category_code}
                          </span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </td>
                      <td style={td}>
                        <StatusBadge status={it.status} />
                      </td>
                      <td style={td} data-testid={`admin-freelance-missing-${it.user_id}`}>
                        {(it.missing || []).length === 0 ? (
                          <span style={{ color: "#009640", fontSize: "0.78rem" }}>Complet ✓</span>
                        ) : (
                          <span style={{ color: "#B45309", fontSize: "0.78rem" }}>
                            Manque : {it.missing.join(", ")}
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, fontSize: "0.72rem", color: "#64748b" }}>
                        {it.updated_at ? new Date(it.updated_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </PrivateLayout>
  );
}

function Kpi({ label, value, icon: Icon, color, testid }) {
  return (
    <div
      className="afr-card"
      style={{ padding: 14, borderLeft: `3px solid ${color}` }}
      data-testid={testid}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Icon size={16} color={color} />
        <div style={{ fontSize: "0.78rem", color: "#64748b" }}>{label}</div>
      </div>
      <div style={{ fontSize: "1.6rem", fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const isVerified = status === "VERIFIED";
  return (
    <span
      data-testid={`admin-freelance-badge-${status}`}
      style={{
        padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontSize: "0.72rem",
        background: isVerified ? "rgba(0,150,64,0.15)" : "rgba(180,83,9,0.15)",
        color: isVerified ? "#065F46" : "#78350F",
      }}
    >
      {status}
    </span>
  );
}

const th = { textAlign: "left", padding: "8px 10px", fontWeight: 600, fontSize: "0.78rem" };
const td = { padding: "8px 10px", borderBottom: "1px solid var(--afr-border,#E2E8F0)" };
